import { Filter, ChevronsRight } from 'lucide-react';
import type { TabId, TabState } from '../types';
import { SUB_REGIONS } from '../utils/regions';
import { getFYOptions, isAllSub, formatSub } from '../utils/filter';
import { useRequests } from '../context/RequestsContext';
interface Props {
  tab: TabId;
  state: TabState & {
    status?: string;
  };
  onUpdate: (updates: Partial<TabState>) => void;
}
function getLabel(tab: TabId, s: TabState & {
  status?: string;
}): string {
  let lbl = s.r;
  const subLbl = formatSub(s.sub);
  if (subLbl) lbl += ' › ' + subLbl;
  if (tab === 'vp' && s.status) lbl += ' · ' + s.status;
  const fyPart = s.fy !== 'All' ? s.fy : '';
  const qPart = s.q !== 'All' ? s.q : '';
  if (fyPart || qPart) lbl += ' · ' + (fyPart && qPart ? fyPart + '-' + qPart : fyPart || qPart);
  return lbl;
}
export function FilterBar({
  tab,
  state,
  onUpdate
}: Props) {
  const {
    requests
  } = useRequests();
  const fyOptions = getFYOptions(requests);
  const quarters = ['All', 'Q1', 'Q2', 'Q3', 'Q4'];
  const statuses = ['All', 'In Progress', 'Closed', 'On Hold', 'Cancelled'];
  const regions = ['Global', 'AMER', 'EMEA', 'JAPAC'];
  const subRegions = SUB_REGIONS[state.r] ?? [];
  function setRegion(r: string) {
    // On region change, reset sub-region selection to the whole region ("All").
    onUpdate({
      r,
      sub: ['All']
    });
  }

  // Toggle a single sub-region on/off. Selecting any sub clears the "All"
  // sentinel; deselecting the last selected sub snaps back to ['All'].
  function toggleSub(s: string) {
    const cur = (state.sub ?? []).filter(x => x !== 'All');
    const next = cur.includes(s) ? cur.filter(x => x !== s) : [...cur, s];
    onUpdate({
      sub: next.length ? next : ['All']
    });
  }
  return <div className="bg-white px-[18px] py-[10px] flex gap-4 flex-wrap items-start border-b border-[#e8eaf0] shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-id="element-140" data-mp="cobbc71t31nx">
      {/* Region */}
      <div className="flex flex-col gap-[6px]" data-id="element-141" data-mp="1o4rotutsv885">
        <div className="text-[9px] font-bold text-[#8892a0] uppercase tracking-[.06em]" data-id="element-142" data-mp="1o3zyayf280oq">Region</div>
        <div className="flex gap-1 flex-wrap" data-id="element-143" data-mp="lqlm2cb0twct">
          {regions.map(r => <button key={r} className={`rpill ${state.r === r ? 'active' : ''}`} onClick={() => setRegion(r)} data-id="element-144" data-mp="qoveif5d2iuq">
              {r}
            </button>)}
        </div>
        {subRegions.length > 0 && <div className="flex items-center gap-[6px] pt-1 flex-wrap" data-id="element-145" data-mp="zqct1t34echb">
            <span className="text-[9px] font-semibold text-[#8892a0] uppercase tracking-[.05em] whitespace-nowrap flex items-center gap-1" data-id="element-146" data-mp="31exw9rgkcjm">
              <ChevronsRight className="w-[10px] h-[10px] text-[#d0d5dd]" data-id="element-147" data-mp="72h111ohx8qv" />Sub-region
            </span>
            <div className="flex gap-1 flex-wrap" data-id="element-148" data-mp="bz7dzg507o82">
              <button className={`srpill ${isAllSub(state.sub) ? 'active' : ''}`} onClick={() => onUpdate({
            sub: ['All']
          })} data-id="element-149" data-mp="gsa6rm1yitn7">
                All {state.r}
              </button>
              {subRegions.map(s => <button key={s} className={`srpill ${state.sub.includes(s) ? 'active' : ''}`} onClick={() => toggleSub(s)} data-id="element-150" data-mp="3cxkqzetmbh70">
                  {s}
                </button>)}
            </div>
          </div>}
      </div>

      {/* Status (VP only) */}
      {tab === 'vp' && <div className="flex flex-col gap-[6px]" data-id="element-151" data-mp="2yxjs24wwyju6">
          <div className="text-[9px] font-bold text-[#8892a0] uppercase tracking-[.06em]" data-id="element-152" data-mp="2tz8izweg0dd0">Status</div>
          <div className="flex gap-1 flex-wrap" data-id="element-153" data-mp="2p7i5xio4fbhw">
            {statuses.map(s => <button key={s} className={`rpill ${(state.status ?? 'All') === s ? 'active' : ''}`} onClick={() => onUpdate({
          status: s
        })} data-id="element-154" data-mp="2k8nksa2olaum">
                {s}
              </button>)}
          </div>
        </div>}

      {/* Created Quarter */}
      <div className="flex flex-col gap-[6px]" data-id="element-155" data-mp="26a74psixi7zt">
        <div className="text-[9px] font-bold text-[#8892a0] uppercase tracking-[.06em]" data-id="element-156" data-mp="26bi7bp0n19pc">Created Quarter</div>
        <div className="flex items-center gap-2 flex-wrap" data-id="element-157" data-mp="e7l8mgwx7fwq">
          <select className="fy-select" value={state.fy} onChange={e => onUpdate({
          fy: e.target.value
        })} data-id="element-158" data-mp="e7346x03tvdq">
            {fyOptions.map(y => <option key={y} value={y} data-id="element-159" data-mp="5dzt2vcxlum9">{y === 'All' ? 'All FY' : 'FY' + y}</option>)}
          </select>
          <div className="w-px h-[18px] bg-[#e8eaf0] flex-shrink-0" data-id="element-160" data-mp="3mm399xkj7t6b" />
          {quarters.map(q => <button key={q} className={`cqfpill ${state.q === q ? 'active' : ''}`} onClick={() => onUpdate({
          q
        })} data-id="element-161" data-mp="3rnso7oavkqzo">
              {q === 'All' ? 'All Q' : q}
            </button>)}
        </div>
      </div>

      {/* Summary label */}
      <div className="text-[10px] text-[#8892a0] ml-auto self-center flex items-center gap-1" data-id="element-162" data-mp="1bw4ppog7qqer">
        <Filter className="w-[11px] h-[11px]" data-id="element-163" data-mp="1gtoh7q1x8zon" />
        {getLabel(tab, state)}
      </div>
    </div>;
}